// IntakePane — full-page chat surface that runs the Creative Director
// agent against the briefing's reserved thread_id.
//
// Closure-stale fix preserved: the assistant turn is appended to history
// using api.chat's *return value* (the persisted message), NOT by reading
// partial/activity/todos from local state in finally — those locals are
// closure-captured at send() time and never update mid-render. Reading
// them in finally was the bug that made users have to reload to see the
// agent's response.

import {
  ArrowUp,
  FileSpreadsheet,
  Loader2,
  Paperclip,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { ChatBubble } from "@/components/ChatBubble";
import { TimelineView } from "@/components/TimelineView";
import { TodoListCard } from "@/components/TodoListCard";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useStore } from "@/lib/store";
import { templateToSystemPrompt } from "@/lib/templates";
import { timelineFromMessage } from "@/lib/timeline";
import type {
  ComposerAttachment,
  Message,
  StreamEvent,
  TimelineSegment,
  TodoItem,
} from "@/lib/types";
import { cn } from "@/lib/utils";

interface HistoryTurn {
  role: "user" | "assistant";
  text: string;
  segments: TimelineSegment[];
  todos?: TodoItem[];
  /** Composer attachments the user sent with this turn — image
   * thumbnails + data-file pills appear above the user bubble so
   * the chat history shows "you sent these with this message". */
  attachments?: ComposerAttachment[];
}

// Each attached file (image OR data) kicks off an immediate upload to the
// backend. The blob URL drives the local thumbnail for images; data files
// render as a doc-icon card with the filename. We track per-attachment
// upload state so the send button can wait for any in-flight uploads + show
// errors inline.
type AttachmentKind = "image" | "data";

interface PendingRef {
  id: string;             // local UUID — stable while in the composer
  file: File;
  kind: AttachmentKind;
  previewUrl?: string;    // blob URL for image thumbnails (revoked on remove/send)
  name: string;
  size: number;
  uploadState: "uploading" | "ready" | "error";
  serverId?: string;      // Tigris id (ref_* for images, data_* for CSV)
  serverUrl?: string;     // presigned URL for images; unused for data files
  rowCount?: number;      // populated for data files after upload — shown in chip
  errorMessage?: string;
}

const uid = () => Math.random().toString(36).slice(2, 10);
const MAX_IMAGE_BYTES = 50 * 1024 * 1024; // Luma's 50MB-per-image cap
const MAX_CSV_BYTES = 10 * 1024 * 1024;   // backend's CSV cap
// xlsx workbooks carry embedded images (lookbook hero shots are 1-3 MB
// each), so the realistic ceiling is much higher. Mirror server's
// XLSX_MAX_BYTES in data_attachments.py.
const MAX_XLSX_BYTES = 50 * 1024 * 1024;
const MAX_REFS = 9; // Luma image_ref array caps at 9 (applies to images only)
const DATA_EXTENSIONS = [".csv", ".tsv", ".xlsx", ".xlsm"];
const DATA_MIMES = new Set([
  "text/csv",
  "application/csv",
  // Browsers often tag CSV as ms-excel — accept it for CSV. xlsx has
  // its own mime listed below.
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel.sheet.macroenabled.12",
]);

function classify(file: File): AttachmentKind | null {
  if (file.type.startsWith("image/")) return "image";
  // Mime is unreliable across browsers — fall back to extension.
  const lower = file.name.toLowerCase();
  if (
    DATA_MIMES.has(file.type) ||
    DATA_EXTENSIONS.some((ext) => lower.endsWith(ext))
  ) {
    return "data";
  }
  return null;
}

export function IntakePane({
  threadId,
  briefingId,
  savedCatalogIds,
  onBriefingUpdated,
  onArtifact,
  onGenerationProgress,
}: {
  threadId: string;
  briefingId: string;
  /** Ids of catalogs already saved to the briefing — passed through
   * to ProductCatalogCard so it renders "Saved" state when applicable. */
  savedCatalogIds?: Set<string>;
  onBriefingUpdated?: () => void;
  /** Live artifact emissions from the agent. kind_subtype is the
   * artifact type (cd_brief / brief / ...); data is the raw payload.
   * Lets the parent surface the agent's output before persistence
   * lands on the briefing record. */
  onArtifact?: (kind: string, data: unknown) => void;
  /** Per-variant Luma generation progress. Fires as soon as
   * generate_images submits each variant ("submitting" placeholder),
   * tracks state changes, and lands with `url` set on phase=ready.
   * The parent uses this to show pending / completed cards in the
   * moodboard ahead of the final artifact event. */
  onGenerationProgress?: (
    ev: Extract<StreamEvent, { type: "image_gen_progress" }>
  ) => void;
}) {
  const { loadBriefings, activeTemplate, setToast, useBrandGraph } =
    useStore();
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [liveSegments, setLiveSegments] = useState<TimelineSegment[]>([]);
  const [history, setHistory] = useState<HistoryTurn[]>([]);
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [attachments, setAttachments] = useState<PendingRef[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Drag-leave fires for child elements too — we count nested dragenter
  // events and only clear the dragging state when we're actually outside
  // the composer. Without this, hovering over a child clears the indicator.
  const dragDepthRef = useRef(0);

  // Free blob URLs when component unmounts so the browser can release
  // the underlying File memory. Only images carry a blob URL; data
  // files have no preview URL (they render as a doc icon).
  useEffect(() => {
    return () => {
      attachments.forEach((a) => {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadOne = useCallback(
    async (localId: string, file: File) => {
      try {
        const [stored] = await api.uploadRefs(threadId, [file]);
        if (!stored) {
          throw new Error("Server returned no ref");
        }
        setAttachments((current) =>
          current.map((a) =>
            a.id === localId
              ? {
                  ...a,
                  uploadState: "ready",
                  serverId: stored.id,
                  // Image refs have presigned URL; data files have
                  // filename + row_count instead. Either way, store
                  // whatever the server returned.
                  serverUrl: stored.url,
                  rowCount: stored.row_count,
                }
              : a
          )
        );
      } catch (err) {
        const errorMessage =
          (err as Error).message || "Upload failed";
        console.error("[intake] ref upload failed", err);
        setAttachments((current) =>
          current.map((a) =>
            a.id === localId
              ? { ...a, uploadState: "error", errorMessage }
              : a
          )
        );
        setToast({
          kind: "error",
          text: `Failed to upload ${file.name}: ${errorMessage}`,
        });
      }
    },
    [threadId, setToast]
  );

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const incoming = Array.from(files);
      // Classify and reject unsupported types up front so the user gets a
      // single clear toast instead of a 400 from the server.
      const accepted: PendingRef[] = [];
      const unsupported: string[] = [];
      const tooLarge: string[] = [];
      // Image cap applies only to images (Luma's image_ref limit).
      // Data files share the composer slot but don't count against
      // image refs — the agent reads them via a different tool.
      const imageCount = attachments.filter((a) => a.kind === "image").length;
      const imageSlotsLeft = MAX_REFS - imageCount;

      for (const file of incoming) {
        const kind = classify(file);
        if (kind === null) {
          unsupported.push(file.name);
          continue;
        }
        const isXlsx = /\.(xlsx|xlsm)$/i.test(file.name);
        const limit =
          kind === "image"
            ? MAX_IMAGE_BYTES
            : isXlsx
              ? MAX_XLSX_BYTES
              : MAX_CSV_BYTES;
        if (file.size > limit) {
          tooLarge.push(file.name);
          continue;
        }
        if (kind === "image" && accepted.filter((a) => a.kind === "image").length >= imageSlotsLeft) {
          continue;
        }
        accepted.push({
          id: "loc_" + uid(),
          file,
          kind,
          previewUrl:
            kind === "image" ? URL.createObjectURL(file) : undefined,
          name: file.name,
          size: file.size,
          uploadState: "uploading",
        });
      }

      if (unsupported.length > 0) {
        setToast({
          kind: "error",
          text: `Unsupported file type: ${unsupported[0]}. Only images and CSV files are accepted.`,
        });
      }
      if (tooLarge.length > 0) {
        const offending = incoming.find((f) => f.name === tooLarge[0]);
        const isImg = offending?.type.startsWith("image/");
        const isXlsx = /\.(xlsx|xlsm)$/i.test(tooLarge[0]);
        const capLabel = isImg ? "50MB" : isXlsx ? "50MB" : "10MB";
        setToast({
          kind: "error",
          text: `${tooLarge[0]} is too large (max ${capLabel}).`,
        });
      }
      if (
        imageSlotsLeft <= 0 &&
        incoming.some((f) => classify(f) === "image")
      ) {
        setToast({
          kind: "error",
          text: `Maximum ${MAX_REFS} reference images per message.`,
        });
      }
      if (accepted.length === 0) return;
      setAttachments((current) => [...current, ...accepted]);
      // Fire uploads in parallel — each updates its own slot when done.
      accepted.forEach((a) => uploadOne(a.id, a.file));
    },
    [attachments, setToast, uploadOne]
  );

  const removeAttachment = useCallback((id: string) => {
    setAttachments((current) => {
      const target = current.find((a) => a.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return current.filter((a) => a.id !== id);
    });
  }, []);

  const onDragEnter = useCallback((e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer.types).includes("Files")) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDragging(true);
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer.types).includes("Files")) return;
    e.preventDefault();
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer.types).includes("Files")) return;
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      if (!Array.from(e.dataTransfer.types).includes("Files")) return;
      e.preventDefault();
      dragDepthRef.current = 0;
      setIsDragging(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles]
  );

  // Load the thread's persisted history on mount / threadId change.
  useEffect(() => {
    if (!threadId) return;
    let cancelled = false;
    setInput("");
    setStreaming(false);
    setLiveSegments([]);
    setTodos([]);
    (async () => {
      try {
        const msgs = await api.listMessages(threadId);
        if (cancelled) return;
        const restored: HistoryTurn[] = msgs
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map((m: Message) => ({
            role: m.role as "user" | "assistant",
            text: typeof m.content === "string" ? m.content : "",
            segments:
              m.role === "assistant" ? timelineFromMessage(m) : [],
            todos: Array.isArray(m.todos) ? m.todos : [],
            attachments: Array.isArray(m.composerAttachments)
              ? m.composerAttachments
              : undefined,
          }));
        setHistory(restored);
      } catch (e) {
        console.error("[intake] failed to load history", e);
        setHistory([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [threadId]);

  // Pin scroll to bottom as text streams.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [liveSegments, history.length, todos.length]);

  const sendText = useCallback(async (
    textOverride?: string,
    extraRefIds?: string[],
    enforcedImageRefs?: string[],
  ) => {
    // Three callers:
    //  - composer "send" button: all overrides undefined → read text
    //    from input + only the user's composer attachments
    //  - "Generate this look": text+extras for the gen prompt PLUS
    //    enforcedImageRefs so the tool-layer guarantees those exact
    //    ref_ids reach Luma regardless of LLM discretion
    //  - "Generate all 4": text+extras only, NO enforcement — each
    //    per-look gen call needs its own subset, agent-driven
    const text = (textOverride ?? input).trim();
    if (!text || streaming || !threadId) return;
    // Block send if any upload is still in flight or errored — surfacing
    // a half-uploaded ref to the agent would mean it sees missing context.
    const stillUploading = attachments.some((a) => a.uploadState === "uploading");
    const hasErrors = attachments.some((a) => a.uploadState === "error");
    if (stillUploading) {
      setToast({ kind: "error", text: "Wait for uploads to finish." });
      return;
    }
    if (hasErrors) {
      setToast({
        kind: "error",
        text: "Some uploads failed — remove or retry them before sending.",
      });
      return;
    }
    // Union composer attachments + extras. Dedupe preserves order: any
    // extras that happen to overlap with composer attachments (unlikely
    // but cheap to handle) don't get passed twice.
    const composerRefIds = attachments
      .map((a) => a.serverId)
      .filter((id): id is string => typeof id === "string");
    const refIds = Array.from(
      new Set([...composerRefIds, ...(extraRefIds ?? [])])
    );
    const sendingAttachments = attachments;
    // Persistable snapshot for the chat-history chip strip. We strip
    // the File object + blob preview URL (only meaningful while the
    // attachment is in the composer) and keep the durable bits.
    const visibleAttachments: ComposerAttachment[] = sendingAttachments
      .filter((a) => a.uploadState === "ready" && a.serverId)
      .map((a) => ({
        kind: a.kind,
        refId: a.serverId!,
        name: a.name,
        url: a.serverUrl,
        rowCount: a.rowCount,
        mimeType: a.file.type || undefined,
      }));
    setInput("");
    setAttachments([]);
    sendingAttachments.forEach((a) => {
      if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
    });
    setHistory((h) => [
      ...h,
      {
        role: "user",
        text,
        segments: [],
        attachments:
          visibleAttachments.length > 0 ? visibleAttachments : undefined,
      },
    ]);
    setStreaming(true);
    setLiveSegments([]);
    setTodos([]);
    let streamErrorText: string | null = null;

    try {
      // Persist the user turn first — api.chat reads prior history from
      // localStorage to build its SSE request body. Attachments piggyback
      // on the persisted message so they survive a page reload.
      await api.appendMessage(threadId, {
        role: "user",
        content: text,
        composerAttachments:
          visibleAttachments.length > 0 ? visibleAttachments : undefined,
      });

      const { message } = await api.chat({
        threadId,
        userText: text,
        attachments: [],
        templatePrompt: activeTemplate
          ? templateToSystemPrompt(activeTemplate)
          : undefined,
        refIds: refIds.length > 0 ? refIds : undefined,
        useBrandGraph,
        enforcedImageRefs:
          enforcedImageRefs && enforcedImageRefs.length > 0
            ? enforcedImageRefs
            : undefined,
        onEvent: (ev: StreamEvent) => {
          // The interleaved timeline drives chat rendering — text + tool
          // segments arrive in agent-emit order so reasoning between tool
          // calls renders as separate bubbles.
          if (ev.type === "segments") {
            setLiveSegments(ev.segments);
          } else if (ev.type === "todos") {
            setTodos(ev.todos);
          } else if (ev.type === "activity") {
            // Stream artifacts up so the workspace's Creative Direction
            // and Briefing tabs can render the agent's output live —
            // without waiting for the briefing-record refetch.
            if (
              ev.entry.kind === "artifact" &&
              ev.entry.kind_subtype !== "ask_user_question"
            ) {
              onArtifact?.(ev.entry.kind_subtype, ev.entry.data);
              onBriefingUpdated?.();
            }
          } else if (ev.type === "briefing_record") {
            onBriefingUpdated?.();
          } else if (ev.type === "image_gen_progress") {
            onGenerationProgress?.(ev);
          }
        },
      });

      // Closure-stale fix preserved: append the assistant turn from the
      // persisted message returned by api.chat (segments, content, todos
      // are all on it). Reading liveSegments here would see the closure-
      // captured empty initial state from send()'s invocation moment.
      if (
        message &&
        (message.content ||
          (message.segments && message.segments.length) ||
          (message.activity && message.activity.length) ||
          (message.todos && message.todos.length))
      ) {
        setHistory((h) => [
          ...h,
          {
            role: "assistant",
            text: typeof message.content === "string" ? message.content : "",
            segments: timelineFromMessage(message),
            todos: Array.isArray(message.todos) ? message.todos : [],
          },
        ]);
      }
    } catch (err) {
      console.error("[intake] stream failed", err);
      streamErrorText = String((err as Error).message || err);
    } finally {
      setLiveSegments([]);
      setTodos([]);
      setStreaming(false);
      if (streamErrorText) {
        const errText = streamErrorText;
        setHistory((h) => [
          ...h,
          {
            role: "assistant",
            text: `⚠ ${errText}`,
            segments: [{ kind: "text", text: `⚠ ${errText}` }],
          },
        ]);
      }
      loadBriefings();
      onBriefingUpdated?.();
    }
  }, [
    input,
    streaming,
    threadId,
    loadBriefings,
    onBriefingUpdated,
    onArtifact,
    onGenerationProgress,
    activeTemplate,
    attachments,
    setToast,
    useBrandGraph,
  ]);

  // Composer button calls send() with no args; ProductCatalogCard and
  // future inline-action UIs call sendText(text, extraRefIds) to fire
  // a programmatic turn. Same flow, same upload-blocking guards.
  const send = useCallback(() => {
    void sendText();
  }, [sendText]);
  // Wrap sendText so its return type matches `(text, extras) => void`
  // expected by child cards (they don't await).
  const handleProgrammaticSend = useCallback(
    (
      text: string,
      extraRefIds?: string[],
      enforcedImageRefs?: string[],
    ) => {
      void sendText(text, extraRefIds, enforcedImageRefs);
    },
    [sendText]
  );


  return (
    <div className="flex flex-1 flex-col min-h-0 bg-surface-subtle">
      {/* Conversation area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 md:px-10 py-6">
        <div className="mx-auto w-full max-w-3xl space-y-5">
          {history.length === 0 && !streaming && liveSegments.length === 0 && (
            <div className="rounded-lg border border-dashed border-border bg-card/40 p-8 text-center">
              <p className="text-sm text-muted-foreground italic">
                Tell the Creative Director what you want to brief — product,
                market, audience cue, anything you have. The agent runs the
                intake from there.
              </p>
            </div>
          )}

          {history.map((m, i) => (
            <div key={i} className="space-y-3">
              {m.role === "user" ? (
                <div className="space-y-1.5">
                  {m.attachments && m.attachments.length > 0 && (
                    <SentAttachmentsStrip attachments={m.attachments} />
                  )}
                  <ChatBubble role="user">{m.text}</ChatBubble>
                </div>
              ) : (
                <>
                  <TimelineView
                    segments={m.segments}
                    live={false}
                    onSendMessage={handleProgrammaticSend}
                    briefingId={briefingId}
                    savedCatalogIds={savedCatalogIds}
                    onCatalogSaved={onBriefingUpdated}
                  />
                  {m.todos && m.todos.length > 0 && (
                    <TodoListCard todos={m.todos} />
                  )}
                </>
              )}
            </div>
          ))}

          {/* Live streaming — interleaved timeline of text + tool calls,
              plus a "thinking" pill while the first event is in flight. */}
          {streaming && liveSegments.length === 0 && todos.length === 0 && (
            <div className="inline-flex items-center gap-2 rounded-lg bg-card border border-border px-3 py-2 text-sm text-muted-foreground italic">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Creative Director is thinking…
            </div>
          )}

          {streaming && liveSegments.length > 0 && (
            <TimelineView
              segments={liveSegments}
              live
              streaming
              onSendMessage={handleProgrammaticSend}
              briefingId={briefingId}
              savedCatalogIds={savedCatalogIds}
              onCatalogSaved={onBriefingUpdated}
            />
          )}

          {streaming && todos.length > 0 && <TodoListCard todos={todos} />}
        </div>
      </div>

      {/* Composer — pinned to bottom. Drag-and-drop is on the outer
          container so the user can drop anywhere over the composer area,
          including over the thumbnail strip. */}
      <div
        className="border-t border-border bg-background"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <div className="mx-auto w-full max-w-3xl px-6 md:px-10 py-4">
          <div
            className={cn(
              "rounded-xl border bg-card shadow-card transition-all",
              "focus-within:ring-2 focus-within:ring-ring/30",
              isDragging
                ? "border-foreground border-dashed ring-2 ring-foreground/20"
                : "border-border"
            )}
          >
            {/* Hidden file input — clicked via the paperclip button */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.csv,.tsv,.xlsx,.xlsm,text/csv,application/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  addFiles(e.target.files);
                }
                // Reset so picking the same file twice still triggers onChange.
                e.target.value = "";
              }}
            />

            {/* Thumbnail strip — image refs render as square previews,
                data files render as horizontal pills with filename. Both
                share upload-state UI (spinner / failed overlay). */}
            {attachments.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 pt-3 pb-3">
                {attachments.map((att) =>
                  att.kind === "image" ? (
                    <ImageThumbnail
                      key={att.id}
                      att={att}
                      onRemove={removeAttachment}
                    />
                  ) : (
                    <DataThumbnail
                      key={att.id}
                      att={att}
                      onRemove={removeAttachment}
                    />
                  )
                )}
              </div>
            )}

            {/* Input row */}
            <div className="flex items-end gap-3 px-4 py-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={streaming || attachments.length >= MAX_REFS}
                className={cn(
                  "shrink-0 rounded-md p-1.5 text-muted-foreground",
                  "hover:bg-accent hover:text-foreground transition-colors",
                  "disabled:opacity-40 disabled:cursor-not-allowed",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                )}
                title={
                  attachments.length >= MAX_REFS
                    ? `Max ${MAX_REFS} reference images`
                    : "Attach reference images"
                }
                aria-label="Attach reference images"
              >
                <Paperclip className="h-4 w-4" />
              </button>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={
                  streaming
                    ? "Agent is responding…"
                    : isDragging
                      ? "Drop images to attach as references…"
                      : "Describe the brief — product, market, audience…"
                }
                disabled={streaming}
                rows={1}
                className={cn(
                  "flex-1 resize-none bg-transparent outline-none text-sm placeholder:text-muted-foreground/70",
                  "min-h-[24px] max-h-32 leading-relaxed"
                )}
              />
              <Button
                onClick={send}
                disabled={streaming || !input.trim()}
                size="icon-sm"
                shape="pill"
                className="shrink-0"
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Thumbnails ─────────────────────────────────────────────────────────

function ImageThumbnail({
  att,
  onRemove,
}: {
  att: PendingRef;
  onRemove: (id: string) => void;
}) {
  return (
    <div
      className={cn(
        "group relative h-16 w-16 shrink-0 overflow-hidden rounded-md border",
        att.uploadState === "error"
          ? "border-destructive"
          : "border-border"
      )}
      title={
        att.uploadState === "error"
          ? `${att.name} — ${att.errorMessage}`
          : att.name
      }
    >
      {att.previewUrl && (
        <img
          src={att.previewUrl}
          alt={att.name}
          className={cn(
            "h-full w-full object-cover transition-opacity",
            att.uploadState !== "ready" && "opacity-60"
          )}
        />
      )}
      {att.uploadState === "uploading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-foreground/20">
          <Loader2 className="h-4 w-4 animate-spin text-background drop-shadow" />
        </div>
      )}
      {att.uploadState === "error" && (
        <div className="absolute inset-0 flex items-center justify-center bg-destructive/30 text-destructive-foreground text-[10px] font-medium tracking-wide uppercase">
          Failed
        </div>
      )}
      <RemoveButton att={att} onRemove={onRemove} />
    </div>
  );
}

function DataThumbnail({
  att,
  onRemove,
}: {
  att: PendingRef;
  onRemove: (id: string) => void;
}) {
  return (
    <div
      className={cn(
        "group relative inline-flex items-center gap-2 rounded-md border bg-card px-3 py-2",
        "max-w-[260px]",
        att.uploadState === "error"
          ? "border-destructive"
          : "border-border"
      )}
      title={
        att.uploadState === "error"
          ? `${att.name} — ${att.errorMessage}`
          : att.name + (att.rowCount ? ` · ${att.rowCount} rows` : "")
      }
    >
      <div className="shrink-0 flex h-6 w-6 items-center justify-center">
        {att.uploadState === "uploading" ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : att.uploadState === "error" ? (
          <X className="h-4 w-4 text-destructive" />
        ) : (
          <FileSpreadsheet className="h-4 w-4 text-foreground/70" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium truncate">{att.name}</p>
        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          {att.uploadState === "uploading"
            ? "Uploading…"
            : att.uploadState === "error"
              ? "Failed"
              : att.rowCount
                ? `${att.rowCount} rows`
                : "Ready"}
        </p>
      </div>
      <RemoveButton att={att} onRemove={onRemove} inline />
    </div>
  );
}

function RemoveButton({
  att,
  onRemove,
  inline = false,
}: {
  att: PendingRef;
  onRemove: (id: string) => void;
  inline?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => onRemove(att.id)}
      className={cn(
        inline
          ? "shrink-0 rounded-full p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          : cn(
              "absolute -right-1.5 -top-1.5 rounded-full",
              "bg-foreground p-0.5 text-background",
              "opacity-0 transition-opacity group-hover:opacity-100 shadow-sm"
            ),
        "focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
      )}
      aria-label={`Remove ${att.name}`}
    >
      <X className="h-3 w-3" />
    </button>
  );
}

// ─── Sent-attachments strip (chat history) ─────────────────────────────
// Above each user bubble that had attachments. Right-aligned to match
// the user bubble's right alignment. Image refs render as a 12x12 thumb,
// data files as a compact pill with filename + row count. Click image
// to open fullsize. If the persisted URL has expired (24h presign), the
// img tag still renders broken — acceptable for v1; future polish: a
// /briefings/.../refs/{id}/presign endpoint for on-demand minting.

function SentAttachmentsStrip({
  attachments,
}: {
  attachments: ComposerAttachment[];
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {attachments.map((att) =>
        att.kind === "image" ? (
          <a
            key={att.refId}
            href={att.url ?? "#"}
            target="_blank"
            rel="noreferrer"
            className={cn(
              "h-12 w-12 shrink-0 overflow-hidden rounded-md border border-border",
              "bg-muted hover:border-foreground/30 transition-colors"
            )}
            title={att.name}
            onClick={(e) => {
              if (!att.url) e.preventDefault();
            }}
          >
            {att.url ? (
              <img
                src={att.url}
                alt={att.name}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                <Paperclip className="h-4 w-4" />
              </div>
            )}
          </a>
        ) : (
          <div
            key={att.refId}
            className={cn(
              "inline-flex items-center gap-2 rounded-md border border-border bg-card",
              "px-3 py-1.5 max-w-[260px]"
            )}
            title={
              att.rowCount
                ? `${att.name} · ${att.rowCount} rows`
                : att.name
            }
          >
            <FileSpreadsheet className="h-3.5 w-3.5 text-foreground/70 shrink-0" />
            <span className="text-xs font-medium truncate">{att.name}</span>
            {att.rowCount && (
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground shrink-0">
                {att.rowCount} rows
              </span>
            )}
          </div>
        )
      )}
    </div>
  );
}

